import classnames from 'classnames';
import { useBlockOrFieldReadOnly } from 'components/common-form/hooks/useBlockOrFieldReadOnly';
import { useData } from 'components/common-form/hooks/useData';
import { useFieldTranslationKey } from 'components/common-form/hooks/useFieldTranslationKey';
import { useIsFieldInvalid } from 'components/common-form/hooks/useIsFieldInvalid';
import { usePatchedFieldHighlight } from 'components/common-form/hooks/usePatchedFieldHighlight';
import { BlockOrientationFormGroup } from 'components/shared/FormGroup';
import { MultiUnderlyingSelector } from 'components/shared/underlyings/selector/multi';
import { useUnderlyingSearchByFormId } from 'hooks/underlyings/use-underlying-search';
import type { Underlying } from 'models/underlying';
import { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';

import type { UnderlyingsFieldProps } from './UnderlyingsField.connect';
import { connectUnderlyings } from './UnderlyingsField.connect';
import { emptyUnderlyings } from './utils';

const Component: FC<UnderlyingsFieldProps> = ({
  node: { id },
  defaultUnderlyingCodeType,
  workspaceType,
  tabIndex,
  tabId,
}) => {
  const { formatMessage } = useIntl();
  const [underlyings = emptyUnderlyings, setUnderlyings] = useData<Underlying[]>(id);
  const isFieldInvalid = useIsFieldInvalid(id);
  const isReadOnly = useBlockOrFieldReadOnly(id);
  const translationKey = useFieldTranslationKey(id);
  const patchedFieldClassName = usePatchedFieldHighlight(id);

  const { search, getOptionsFromPastedLabels } = useUnderlyingSearchByFormId(tabId, id);

  const placeholder = useMemo(() => formatMessage({ id: 'underlying.placeholder' }), [formatMessage]);

  return (
    <>
      <BlockOrientationFormGroup
        inputId={id}
        label={`form.${translationKey}` as any}
        className={classnames('mb-2', patchedFieldClassName)}
        workspaceType={workspaceType}
      >
        {!isReadOnly && (
          <MultiUnderlyingSelector
            id={id}
            onChange={setUnderlyings}
            underlyings={underlyings}
            placeholder={placeholder}
            hasErrors={isFieldInvalid}
            defaultUnderlyingCodeType={defaultUnderlyingCodeType}
            tabIndex={tabIndex}
            search={search}
            getOptionsFromPastedLabels={getOptionsFromPastedLabels}
          />
        )}
      </BlockOrientationFormGroup>
    </>
  );
};

Component.displayName = 'RawUnderlyingsField';

export const UnderlyingsSearchField = connectUnderlyings(Component);
UnderlyingsSearchField.displayName = 'UnderlyingsSearch';
